/**
 * 프로그램 내용 슬라이드 탭이 열려 있는 동안 같은 scope의 행 구조 편집을 막기 위한 localStorage 락.
 * refCount로 동일 scope에 탭이 여러 개일 때를 처리하고, hb로 비정상 종료 시 stale 정리한다.
 *
 * 학습내용 셀(annualCellKey)별 «프레젠테이션 중» 표시는 워크스페이스 단위 전역 맵에 둔다.
 * (프로그램 표·연간진도표 등 서로 다른 scopeId에서도 동일 행에 오버레이가 맞물리게 함)
 */

const PREFIX = 'cp.programSlideEditScopeLock.v1:';

/** 워크스페이스별 annualCellKey → 열린 프레젠테이션 탭 수 */
const OVERLAY_PREFIX = 'cp.programSlidePresentationOverlay.v1:';

/** 슬라이드 탭이 비정상 종료되면 하트비트가 멈추고, 이 시간이 지나면 localStorage 락·오버레이를 무효로 본다 */
const SLIDE_TAB_LOCAL_STORAGE_STALE_MS = 90_000;

interface LockPayload {
  refCount: number;
  /** 마지막으로 이 scope 슬라이드 탭이 살아 있음을 알린 시각(ms) */
  hb?: number;
}

interface OverlayPayload {
  counts: Record<string, number>;
  /** 마지막으로 슬라이드 탭이 살아 있음을 알린 시각(ms) */
  hb?: number;
}

function slideTabHeartbeatStale(hb: number | undefined): boolean {
  const t = typeof hb === 'number' ? hb : 0;
  return t <= 0 || Date.now() - t > SLIDE_TAB_LOCAL_STORAGE_STALE_MS;
}

function overlayHeartbeatStale(payload: Partial<OverlayPayload>): boolean {
  return slideTabHeartbeatStale(payload.hb);
}

function lockPayloadHeartbeatStale(payload: Partial<LockPayload>): boolean {
  return slideTabHeartbeatStale(payload.hb);
}

function lockKey(workspaceId: string, scopeId: string): string {
  return `${PREFIX}${workspaceId.trim()}:${encodeURIComponent(scopeId.trim())}`;
}

function overlayStorageKey(workspaceId: string): string {
  return `${OVERLAY_PREFIX}${workspaceId.trim()}`;
}

export function programSlideEditScopeLockStorageKeyPrefix(): string {
  return PREFIX;
}

export function programSlidePresentationOverlayStorageKeyPrefix(): string {
  return OVERLAY_PREFIX;
}

function bumpPresentationOverlayAnnualCount(workspaceId: string, annualCellKey: string, delta: number): void {
  const sid = workspaceId.trim();
  const ak = annualCellKey.trim();
  if (!sid || !ak || delta === 0) return;
  try {
    const k = overlayStorageKey(sid);
    const raw = window.localStorage.getItem(k);
    let counts: Record<string, number> = {};
    if (raw) {
      try {
        const p = JSON.parse(raw) as Partial<OverlayPayload>;
        if (p.counts && typeof p.counts === 'object' && p.counts !== null) {
          counts = { ...p.counts };
        }
      } catch {
        counts = {};
      }
    }
    counts[ak] = (counts[ak] ?? 0) + delta;
    if (counts[ak] <= 0) delete counts[ak];
    if (Object.keys(counts).length === 0) {
      window.localStorage.removeItem(k);
    } else {
      window.localStorage.setItem(k, JSON.stringify({ counts, hb: Date.now() }));
    }
  } catch {
    /* ignore */
  }
}

/**
 * 하트비트만 갱신한다. 열린 슬라이드 탭에서 주기적으로 호출한다.
 */
export function touchProgramSlidePresentationOverlayHeartbeat(workspaceId: string): void {
  const sid = workspaceId.trim();
  if (!sid) return;
  try {
    const k = overlayStorageKey(sid);
    const raw = window.localStorage.getItem(k);
    if (!raw) return;
    const p = JSON.parse(raw) as Partial<OverlayPayload>;
    const c = p.counts;
    if (!c || typeof c !== 'object') {
      window.localStorage.removeItem(k);
      return;
    }
    const nextCounts: Record<string, number> = {};
    for (const [key, n] of Object.entries(c)) {
      if (typeof n === 'number' && n > 0) nextCounts[key] = n;
    }
    if (Object.keys(nextCounts).length === 0) {
      window.localStorage.removeItem(k);
      return;
    }
    window.localStorage.setItem(k, JSON.stringify({ counts: nextCounts, hb: Date.now() }));
  } catch {
    /* ignore */
  }
}

/** 하트비트가 끊긴 오버레이 저장을 제거한다(effect·주기 호출용). */
export function pruneStalePresentationOverlayStorage(workspaceId: string): boolean {
  const sid = workspaceId.trim();
  if (!sid) return false;
  try {
    const k = overlayStorageKey(sid);
    const raw = window.localStorage.getItem(k);
    if (!raw) return false;
    const p = JSON.parse(raw) as Partial<OverlayPayload>;
    if (!overlayHeartbeatStale(p)) return false;
    window.localStorage.removeItem(k);
    return true;
  } catch {
    try {
      window.localStorage.removeItem(overlayStorageKey(sid));
    } catch {
      /* ignore */
    }
    return true;
  }
}

function readPresentationOverlayAnnualCellKeysAfterPrune(workspaceId: string): string[] {
  const sid = workspaceId.trim();
  if (!sid) return [];
  try {
    const raw = window.localStorage.getItem(overlayStorageKey(sid));
    if (!raw) return [];
    const p = JSON.parse(raw) as Partial<OverlayPayload>;
    if (overlayHeartbeatStale(p)) return [];
    const c = p.counts;
    if (!c || typeof c !== 'object') return [];
    return Object.entries(c)
      .filter(([, n]) => typeof n === 'number' && n > 0)
      .map(([key]) => key);
  } catch {
    return [];
  }
}

/**
 * 슬라이드 탭이 열려 있는 행(annualCellKey) 목록 — 프로그램 표·연간진도표 공통.
 * (비정상 종료로 남은 값은 하트비트 만료 시 prune 후 빈 목록이 된다.)
 */
export function getPresentationOverlayAnnualCellKeys(workspaceId: string): string[] {
  pruneStalePresentationOverlayStorage(workspaceId);
  return readPresentationOverlayAnnualCellKeysAfterPrune(workspaceId);
}

/**
 * 같은 scope 슬라이드 탭이 열려 있을 때 주기적으로 호출한다. 하트비트가 끊기면 행 구조 락은 stale로 정리된다.
 */
export function touchProgramSlideEditScopeLockHeartbeat(workspaceId: string, scopeId: string | null | undefined): void {
  const sid = workspaceId.trim();
  const sc = (scopeId ?? '').trim();
  if (!sid || !sc) return;
  try {
    const k = lockKey(sid, sc);
    const raw = window.localStorage.getItem(k);
    if (!raw) return;
    const parsed = JSON.parse(raw) as Partial<LockPayload>;
    if (lockPayloadHeartbeatStale(parsed)) {
      window.localStorage.removeItem(k);
      return;
    }
    const refCount = typeof parsed.refCount === 'number' ? parsed.refCount : 0;
    if (refCount <= 0) {
      window.localStorage.removeItem(k);
      return;
    }
    window.localStorage.setItem(k, JSON.stringify({ refCount, hb: Date.now() }));
  } catch {
    try {
      window.localStorage.removeItem(lockKey(sid, sc));
    } catch {
      /* ignore */
    }
  }
}

/** 하트비트가 끊긴 scope 락 항목을 제거한다. 제거했으면 true. */
export function pruneStaleProgramSlideEditScopeLock(workspaceId: string, scopeId: string): boolean {
  const sid = workspaceId.trim();
  const sc = scopeId.trim();
  if (!sid || !sc) return false;
  try {
    const k = lockKey(sid, sc);
    const raw = window.localStorage.getItem(k);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as Partial<LockPayload>;
    if (!lockPayloadHeartbeatStale(parsed)) return false;
    window.localStorage.removeItem(k);
    return true;
  } catch {
    try {
      window.localStorage.removeItem(lockKey(sid, sc));
    } catch {
      /* ignore */
    }
    return true;
  }
}

export function acquireProgramSlideEditScopeLock(
  workspaceId: string,
  scopeId: string | null | undefined,
  annualCellKey?: string | null
): void {
  const sid = workspaceId.trim();
  if (!sid) return;
  const sc = (scopeId ?? '').trim();
  const ak = (annualCellKey ?? '').trim();

  if (sc) {
    try {
      const k = lockKey(sid, sc);
      const raw = window.localStorage.getItem(k);
      let refCount = 0;
      if (raw) {
        try {
          const parsed = JSON.parse(raw) as Partial<LockPayload>;
          if (lockPayloadHeartbeatStale(parsed)) {
            refCount = 0;
          } else if (typeof parsed.refCount === 'number' && parsed.refCount > 0) {
            refCount = parsed.refCount;
          }
        } catch {
          refCount = 0;
        }
      }
      refCount += 1;
      window.localStorage.setItem(k, JSON.stringify({ refCount, hb: Date.now() }));
    } catch {
      /* ignore */
    }
  }

  if (ak) {
    bumpPresentationOverlayAnnualCount(sid, ak, +1);
  }
}

export function releaseProgramSlideEditScopeLock(
  workspaceId: string,
  scopeId: string | null | undefined,
  annualCellKey?: string | null
): void {
  const sid = workspaceId.trim();
  if (!sid) return;
  const sc = (scopeId ?? '').trim();
  const ak = (annualCellKey ?? '').trim();

  if (sc) {
    try {
      const k = lockKey(sid, sc);
      const raw = window.localStorage.getItem(k);
      if (raw) {
        let refCount = 0;
        try {
          const parsed = JSON.parse(raw) as Partial<LockPayload>;
          if (lockPayloadHeartbeatStale(parsed)) {
            window.localStorage.removeItem(k);
            refCount = 0;
          } else {
            refCount = typeof parsed.refCount === 'number' ? parsed.refCount : 0;
          }
        } catch {
          window.localStorage.removeItem(k);
          refCount = 0;
        }
        if (refCount > 0) {
          refCount -= 1;
          if (refCount <= 0) {
            window.localStorage.removeItem(k);
          } else {
            const hb = Date.now();
            window.localStorage.setItem(k, JSON.stringify({ refCount, hb }));
          }
        }
      }
    } catch {
      /* ignore */
    }
  }

  if (ak) {
    bumpPresentationOverlayAnnualCount(sid, ak, -1);
  }
}

export function isProgramSlideEditScopeLocked(workspaceId: string, scopeId: string): boolean {
  const sid = workspaceId.trim();
  const sc = scopeId.trim();
  if (!sid || !sc) return false;
  try {
    const raw = window.localStorage.getItem(lockKey(sid, sc));
    if (!raw) return false;
    const parsed = JSON.parse(raw) as Partial<LockPayload>;
    if (lockPayloadHeartbeatStale(parsed)) return false;
    return typeof parsed.refCount === 'number' && parsed.refCount > 0;
  } catch {
    return false;
  }
}
